import _ from "lodash";
import { images } from "config/initializers/images.app";
import { TemplateList, nonScorables } from "schema/TemplateSchema.react";
import { ComponentDefaults } from "schema/FormSchema.react";
import {
    CREATE_DECK,
    CLEAR_DECK,
    UPDATE_DECK,
    LOAD_DECK,
    FETCH_IMAGES,
    EDITOR_RENAME_DECK,
    ADD_SLIDE,
    DUPLICATE_SLIDE,
    RESET_SLIDE,
    DELETE_SLIDE,
    CHANGE_TEMPLATE,
    CHANGE_NAV,
    CHANGE_LANGUAGE,
    CHANGE_MINTIME,
} from "./DeckEditor.action.react";
import { REHYDRATE } from "redux-persist/constants";

const initialDeckState = {
    id: "",
    language: "en",
    name: "",
    readerType: "",
    backgroundImage: "",
    overlayImage: "",
    presenter: "",
    feedback: true,
    navEnabled: true,
    voEnabled: false,
    jumpAhead: false,
    translationEnabled: false,
    colors: {
        base: "#ffffff",
        baseText: "#212121",
        primary: "#0097a7",
        primaryText: "#fff",
        primaryVariation: "#00838f",
        secondary: "#454545",
        secondaryText: "#ffffff",
    },
    content: [],
    translations: { en: [] },
    images: [
        { id: "default17800", image: images.default17800 },
        { id: "default10000", image: images.default10000 },
        { id: "default7500", image: images.default7500 },
        { id: "default5625", image: images.default5625 },
        { id: "default5000", image: images.default5000 },
        { id: "default2500", image: images.default2500 },
    ],
    fetchCompleted: false,
    error: null,

    passingPercentage: null,
    currentSlide: 0,
    resultRange: { minValue: 0, maxValue: 100, masterTags: [] },
};

const createEmptyDeck = (state, action) => {
    let qdfDefaultSlide = {
        paragraph: ComponentDefaults.paragraph,
    };
    let titleDefaultSlide = {
        title: ComponentDefaults.title,
    };
    let quizDefaultSlide = {
        question: ComponentDefaults.question,
        options: ComponentDefaults.options,
        feedback: ComponentDefaults.feedback,
    };
    let brancherDefaultSlide = {
        question: ComponentDefaults.question,
        popupitems: ComponentDefaults.popupitems,
    };
    let inputFieldDefaultSlide = {
        question: ComponentDefaults.question,
    };
    let taggedDefaultSlide = {
        question: ComponentDefaults.question,
        taggedselect: ComponentDefaults.taggedselect,
    };
    let rangeDefaultSlide = {
        question: ComponentDefaults.question,
        rangeselect: ComponentDefaults.rangeselect,
    };
    let readerType = action.data.readerType.toLowerCase();

    let oTemplate = "paragraph";
    let oDefaultSlide = qdfDefaultSlide;
    switch (readerType) {
        case "story":
            oTemplate = "title";
            oDefaultSlide = titleDefaultSlide;
            break;
        case "quiz":
        case "game":
        case "poll":
            oTemplate = "single_select";
            oDefaultSlide = quizDefaultSlide;
            break;
        case "survey":
        case "audit":
        case "assessment":
        case "submission":
        case "feedback":
            oTemplate = "input_field";
            oDefaultSlide = inputFieldDefaultSlide;
            break;
        case "calculator":
            oTemplate = "range_select";
            oDefaultSlide = rangeDefaultSlide;
            break;
        case "profiler":
        case "recommendation":
            oTemplate = "tagged_select";
            oDefaultSlide = taggedDefaultSlide;
            break;
        case "linklist":
            oTemplate = "brancher";
            oDefaultSlide = brancherDefaultSlide;
            break;
        default:
    }

    return {
        ...state,
        id: action.data.shortid.deckId,
        name: action.data.name,
        readerType: readerType,
        content: [
            {
                slideId: action.data.shortid.firstSlideId,
                slideSeq: 0,
                template: oTemplate,
                voiceover: null,
                tts_audio: "",
                minTime: 0,
                nav: {
                    next: true,
                    previous: true,
                },
                data: oDefaultSlide,
                comments: [],
            },
        ],
        error: action.err,
        currentSlide: 0,
    };
};

const parseRow = (row, idx, readerType, shortId) => {
    let dataHash = {
        icon: row.icon,
        title: row.title === undefined ? row.header : row.title,
        question: row.question,
        feedback: [
            row.correct_feedback === undefined
                ? "That is correct."
                : row.correct_feedback,
            row.incorrect_feedback === undefined
                ? "That is incorrect."
                : row.incorrect_feedback,
            row.survey_feedback === undefined
                ? "Thanks for your answer."
                : row.survey_feedback,
        ],
        purpose: "Test Understanding",
    };
    let options = _.filter(_.toPairs(row), (pair) => {
        return (
            pair[0].indexOf("Option") !== -1 ||
            pair[0].indexOf("option") !== -1 ||
            pair[0].indexOf("text") !== -1
        );
    });
    switch (row.qtype) {
        case "single_select":
            dataHash["options"] = _.map(options, (option, idx) => {
                return {
                    text: option[1],
                    correct:
                        idx ===
                            (row.correct !== undefined &&
                                parseInt(row.correct, 0) - 1) &&
                        nonScorables.indexOf(readerType) === -1
                            ? "checked"
                            : "",
                };
            });
            break;
        case "fill_in_the_blanks":
        default:
            dataHash["answer"] =
                options[
                    parseInt(
                        /* istanbul ignore next - basic error catcher */
                        row.correct === undefined ? 1 : row.correct,
                        0
                    ) - 1
                ][1];
    }
    return {
        slideId: shortId,
        slideSeq: idx,
        template: row.qtype,
        voiceover: null,
        tts_audio: "",
        minTime: 0,
        nav: {
            next: true,
            previous: true,
        },
        data: dataHash,
        comments: [],
    };
};

const buildDeck = (state, action) => {
    let content = [];
    switch (action.data.readerType.toLowerCase()) {
        case "survey":
            _.each(action.data.contentHash, (row, idx) => {
                content.push(parseRow(row, idx, "survey", row.id));
            });
            break;
        default:
            _.each(action.data.contentHash, (row, idx) => {
                content.push(parseRow(row, idx, "assessment", row.id));
            });
    }
    return {
        ...state,
        id: action.data.shortid.deckId,
        name: action.data.name,
        readerType: action.data.readerType.toLowerCase(),
        content: content,
        error: action.err,
    };
};

const addSlide = (state, action) => {
    let deck = _.cloneDeep(state);
    let newSlideIndex = deck.currentSlide + 1;
    let newContent = deck.content;
    for (let i = newSlideIndex; i < newContent.length; i++) {
        /* istanbul ignore next - basic for loop */
        newContent[i].slideSeq++;
    }
    let qdfDefaultSlide = {
        paragraph: ComponentDefaults.paragraph,
    };
    let quizDefaultSlide = {
        question: ComponentDefaults.question,
        options: ComponentDefaults.options,
        feedback: ComponentDefaults.feedback,
    };
    let brancherDefaultSlide = {
        question: ComponentDefaults.question,
        options: ComponentDefaults.options,
    };
    let inputFieldDefaultSlide = {
        question: ComponentDefaults.question,
    };
    let taggedDefaultSlide = {
        question: ComponentDefaults.question,
        taggedselect: ComponentDefaults.taggedselect,
    };
    let rangeDefaultSlide = {
        question: ComponentDefaults.question,
        rangeselect: ComponentDefaults.rangeselect,
    };
    let oTemplate = "paragraph";
    let oDefaultSlide = qdfDefaultSlide;
    switch (deck.readerType) {
        case "story":
            oTemplate = "paragraph";
            oDefaultSlide = qdfDefaultSlide;
            break;
        case "quiz":
        case "game":
        case "poll":
            oTemplate = "single_select";
            oDefaultSlide = quizDefaultSlide;
            break;
        case "survey":
        case "audit":
        case "submission":
        case "feedback":
        case "assessment":
            oTemplate = "input_field";
            oDefaultSlide = inputFieldDefaultSlide;
            break;
        case "calculator":
            oTemplate = "range_select";
            oDefaultSlide = rangeDefaultSlide;
            break;
        case "profiler":
        case "recommendation":
            oTemplate = "tagged_select";
            oDefaultSlide = taggedDefaultSlide;
            break;
        case "linklist":
            oTemplate = "brancher";
            oDefaultSlide = brancherDefaultSlide;
            break;
        default:
    }
    newContent.splice(newSlideIndex, 0, {
        slideId: action.data.slideId,
        slideSeq: newSlideIndex,
        template: oTemplate,
        voiceover: null,
        tts_audio: "",
        minTime: 0,
        nav: {
            next: true,
            previous: true,
        },
        data: oDefaultSlide,
        comments: [],
    });
    return {
        content: newContent,
        currentSlide: newSlideIndex,
    };
};
const duplicateSlide = (state, action) => {
    let deck = _.cloneDeep(state);
    let newSlideIndex = deck.currentSlide + 1;
    let newContent = deck.content;
    for (let i = newSlideIndex; i < newContent.length; i++) {
        /* istanbul ignore next - basic for loop */
        newContent[i].slideSeq++;
    }
    let duplicateSlide = {
        ..._.cloneDeep(newContent[deck.currentSlide]),
        slideId: action.data.slideId,
        slideSeq: newSlideIndex,
    };
    newContent.splice(newSlideIndex, 0, duplicateSlide);
    return {
        content: newContent,
        currentSlide: newSlideIndex,
    };
};
const resetSlide = (state, action) => {
    let deck = _.cloneDeep(state);
    let currentSlide = deck.currentSlide;
    let newContent = deck.content;
    let template = newContent[currentSlide].template;
    let templateElements = TemplateList[template].components;
    let newData = {};
    _.each(templateElements, (elm) => {
        /* istanbul ignore next - UI Effect, no testing required */
        if (
            ComponentDefaults[elm] === "Please put in a title" &&
            TemplateList[template].questionable
        ) {
            newData[elm] = ComponentDefaults[template];
        } else {
            newData[elm] = ComponentDefaults[elm];
        }
    });
    newContent[currentSlide].data = newData;
    newContent[currentSlide].comments = [];
    /* istanbul ignore next - UI Effect, no testing required */
    if (
        newContent[currentSlide].template === "brancher_template" ||
        newContent[currentSlide].template === "internal_link"
    )
        newContent[currentSlide].nav = {
            previous: false,
            next: false,
        };
    return {
        content: newContent,
    };
};

const deleteSlide = (state, action) => {
    let deck = _.cloneDeep(state);
    let newContent = deck.content;
    if (newContent.length > 1) {
        for (let i = deck.currentSlide; i < newContent.length; i++) {
            newContent[i].slideSeq--;
        }
        newContent.splice(deck.currentSlide, 1);
        return {
            content: newContent,
            currentSlide: Math.max(0, deck.currentSlide - 1),
        };
    } else {
        return {
            content: newContent,
            currentSlide: deck.currentSlide,
        };
    }
};
const changeTemplate = (state, action) => {
    let deck = _.cloneDeep(state);
    let currentSlide = deck.currentSlide;
    let newContent = deck.content;
    let newTemplate = action.data.newTemplate;
    newContent[currentSlide].template = newTemplate;
    let templateElements = TemplateList[newTemplate].components;
    let newData = {};
    _.each(templateElements, (elm) => {
        /* istanbul ignore next - UI Effect, no testing required */
        if (
            ComponentDefaults[elm] === "Please put in a title" &&
            TemplateList[newTemplate].questionable
        ) {
            newData[elm] = ComponentDefaults[newTemplate];
        } else {
            newData[elm] = ComponentDefaults[elm];
        }
    });
    newContent[currentSlide].data = newData;
    /* istanbul ignore next - UI Effect, no testing required */
    if (
        newContent[currentSlide].template === "brancher_template" ||
        newContent[currentSlide].template === "internal_link"
    )
        newContent[currentSlide].nav = {
            previous: false,
            next: false,
        };
    return {
        content: newContent,
    };
};
const changeNav = (state, action) => {
    let deck = _.cloneDeep(state);
    let currentSlide = deck.currentSlide;
    let newContent = deck.content;
    newContent[currentSlide].nav = action.data.nav;
    return {
        content: newContent,
    };
};
const changeLanguage = (state, action) => {
    let deck = _.cloneDeep(state);
    let newContent = deck.content;
    let langCode = action.data.language;
    let newTranslations = deck.translations;
    /* istanbul ignore next - basic error check, else testing not required */
    if (deck.language === "en") {
        newTranslations["en"] = deck.content;
    }
    /* istanbul ignore next - To be manually tested */
    newContent =
        deck.translations[langCode] !== undefined //If translation exists
            ? deck.translations[langCode] // Make that the current content
            : deck.content; // Else do not change
    return {
        content: newContent,
        translations: newTranslations,
        language: langCode,
    };
};
const changeMinTime = (state, action) => {
    let deck = _.cloneDeep(state);
    let currentSlide = deck.currentSlide;
    let newContent = deck.content;
    newContent[currentSlide].minTime = action.data.minTime;
    return {
        content: newContent,
    };
};

const DeckEditorReducer = (state = initialDeckState, action) => {
    switch (action.type) {
        /* istanbul ignore next - react-persist function */
        case REHYDRATE:
            let deck = action.payload.deck;
            if (deck) {
                return {
                    ...state,
                    ...deck,
                };
            } else {
                return state;
            }
        case CREATE_DECK:
            if (action.data.contentHash === undefined) {
                return createEmptyDeck(state, action);
            } else {
                return buildDeck(state, action);
            }
        case CLEAR_DECK:
            return initialDeckState;
        case UPDATE_DECK:
            return _.assign({}, state, action.data);
        case EDITOR_RENAME_DECK:
            return _.assign({}, state, action.data);
        case LOAD_DECK:
            return _.merge({}, action.data, {
                fetchCompleted: true,
                error: null,
                currentSlide: 0,
                images: initialDeckState.images,
            });
        case FETCH_IMAGES:
            return Object.assign({}, state, {
                images: [...state.images, action.data],
            });
        case ADD_SLIDE:
            return _.assign({}, state, addSlide(state, action));
        case DUPLICATE_SLIDE:
            return _.assign({}, state, duplicateSlide(state, action));
        case RESET_SLIDE:
            return _.assign({}, state, resetSlide(state, action));
        case DELETE_SLIDE:
            return _.assign({}, state, deleteSlide(state, action));
        case CHANGE_TEMPLATE:
            return _.assign({}, state, changeTemplate(state, action));
        case CHANGE_NAV:
            return _.assign({}, state, changeNav(state, action));
        case CHANGE_LANGUAGE:
            return _.assign({}, state, changeLanguage(state, action));
        case CHANGE_MINTIME:
            return _.assign({}, state, changeMinTime(state, action));
        default:
            return state;
    }
};

export { DeckEditorReducer };
